import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, Pencil, Plus, Trash2, Upload, Star, History } from "lucide-react";
import { api, apiError, fmtDate, fmtEUR, API, plainToHtml, htmlToPlain } from "../lib/api";
import { PageHeader } from "./AdminLayout";
import { RichTextEditor } from "../components/admin/RichTextEditor";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const STATUS = { brouillon: { label: "Brouillon", cls: "bg-amber-100 text-amber-800" }, publie: { label: "Publié", cls: "bg-green-100 text-green-800" }, archive: { label: "Archivé", cls: "bg-gray-200 text-gray-700" } };

const EMPTY = {
    name: "", slug: "", short_description: "", description: "", description_html: "", category: "", brand: "BODYCOACHIA",
    benefits: "", specsText: "", journeyText: "", price_eur: "", currency: "EUR",
    merchants: [], featured: false, order: 99,
};

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const pairs = (s) => lines(s).map((l) => { const [label, ...v] = l.split("|"); return { label: label.trim(), value: v.join("|").trim() }; }).filter((x) => x.label && x.value);
const journeyParse = (s) => lines(s).map((l) => { const [title, ...v] = l.split("|"); return { title: title.trim(), text: v.join("|").trim() }; }).filter((x) => x.title && x.text);

function fromProduct(p) {
    return {
        name: p.name, slug: p.slug, short_description: p.short_description || "", description: p.description || "",
        description_html: p.description_html || plainToHtml(p.description),
        category: p.category || "", brand: p.brand || "BODYCOACHIA",
        benefits: (p.benefits || []).join("\n"),
        specsText: (p.specs || []).map((s) => `${s.label} | ${s.value}`).join("\n"),
        journeyText: (p.journey || []).map((j) => `${j.title} | ${j.text}`).join("\n"),
        price_eur: p.price_cents != null ? (p.price_cents / 100).toFixed(2).replace(".", ",") : "",
        currency: p.currency || "EUR",
        merchants: (p.merchants || []).map((m) => ({ ...m })),
        featured: !!p.featured, order: p.order ?? 99,
    };
}

function toPayload(f) {
    return {
        name: f.name, slug: f.slug, short_description: f.short_description,
        description: htmlToPlain(f.description_html) || f.description, description_html: f.description_html,
        category: f.category, brand: f.brand,
        benefits: lines(f.benefits), specs: pairs(f.specsText), journey: journeyParse(f.journeyText),
        price_cents: f.price_eur ? Math.round(parseFloat(String(f.price_eur).replace(",", ".")) * 100) : null,
        currency: f.currency,
        merchants: f.merchants.map((m, i) => ({ ...m, priority: i + 1 })),
        featured: f.featured, order: f.order,
    };
}

function MerchantEditor({ merchants, onChange }) {
    const set = (i, k, v) => onChange(merchants.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <Label className="text-base font-semibold text-ink">Marchands (Amazon, etc.)</Label>
                <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => onChange([...merchants, { name: "Amazon", url: "", label: "", affiliate: false, active: false, note: "" }])} data-testid="merchant-add">
                    <Plus size={13} /> Ajouter un marchand
                </Button>
            </div>
            {merchants.length === 0 && <p className="text-xs text-ink-soft">Aucun marchand : le bouton d'achat affichera « Lien d'achat bientôt disponible ».</p>}
            {merchants.map((m, i) => (
                <div key={i} className="rounded-xl border border-border bg-pearl/50 p-4 space-y-3" data-testid={`merchant-row-${i}`}>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Marchand</Label><Input value={m.name} onChange={(e) => set(i, "name", e.target.value)} data-testid={`merchant-name-${i}`} /></div>
                        <div><Label>Libellé du bouton (vide = « Acheter sur {m.name || "…"} »)</Label><Input value={m.label} onChange={(e) => set(i, "label", e.target.value)} data-testid={`merchant-label-${i}`} /></div>
                    </div>
                    <div><Label>URL produit (HTTPS)</Label><Input value={m.url} onChange={(e) => set(i, "url", e.target.value)} placeholder="https://www.amazon.fr/…" data-testid={`merchant-url-${i}`} /></div>
                    <div><Label>Note interne</Label><Input value={m.note || ""} onChange={(e) => set(i, "note", e.target.value)} /></div>
                    <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 text-sm text-ink-soft"><Switch checked={m.active} onCheckedChange={(v) => set(i, "active", v)} data-testid={`merchant-active-${i}`} /> Actif</label>
                        <label className="flex items-center gap-2 text-sm text-ink-soft"><Switch checked={m.affiliate} onCheckedChange={(v) => set(i, "affiliate", v)} data-testid={`merchant-affiliate-${i}`} /> Lien affilié</label>
                        <button type="button" className="ml-auto text-xs text-destructive" onClick={() => onChange(merchants.filter((_, j) => j !== i))} data-testid={`merchant-remove-${i}`}>Retirer</button>
                    </div>
                    {m.verified_at && <p className="text-[11px] text-ink-soft">Dernière vérification : {fmtDate(m.verified_at)}</p>}
                </div>
            ))}
        </div>
    );
}

export default function Boutique() {
    const [products, setProducts] = useState(null);
    const [stats, setStats] = useState(null);
    const [tab, setTab] = useState("produits");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [history, setHistory] = useState(null);
    const fileInputs = useRef({});

    const load = () => {
        api.get("/admin/boutique/products").then((r) => setProducts(r.data)).catch((e) => toast.error(apiError(e)));
        api.get("/admin/boutique/stats").then((r) => setStats(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const payload = toPayload(form);
            if (editing.product_id) await api.patch(`/admin/boutique/products/${editing.product_id}`, payload);
            else await api.post("/admin/boutique/products", payload);
            toast.success("Produit enregistré.");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const setStatus = async (p, status) => {
        try {
            await api.post(`/admin/boutique/products/${p.product_id}/status`, { status });
            toast.success(status === "publie" ? "Produit publié sur la boutique." : "Produit retiré de la boutique.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const duplicate = async (p) => {
        try {
            await api.post(`/admin/boutique/products/${p.product_id}/duplicate`);
            toast.success("Produit dupliqué en brouillon.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const uploadMedia = async (p, file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/admin/boutique/products/${p.product_id}/media`, fd);
            toast.success("Média ajouté à la galerie.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const setPrimary = async (p, mid) => {
        try {
            await api.post(`/admin/boutique/products/${p.product_id}/media/${mid}/primary`);
            toast.success("Image principale définie.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const removeMedia = async (p, mid) => {
        try {
            await api.delete(`/admin/boutique/products/${p.product_id}/media/${mid}`);
            toast.success("Média retiré.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    return (
        <div data-testid="admin-boutique">
            <PageHeader title="Boutique" subtitle="Produits, galeries, liens marchands et statistiques réelles. Toute modification publiée est immédiatement visible sur /boutique.">
                <Button onClick={() => { setForm(EMPTY); setEditing({}); }} className="bg-forest text-ivory gap-2" data-testid="product-add-button"><Plus size={16} /> Nouveau produit</Button>
            </PageHeader>
            <div className="flex gap-2 mb-6">
                {[["produits", "Produits"], ["stats", "Statistiques"]].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-full text-sm border ${tab === k ? "bg-forest text-ivory border-forest" : "border-border text-ink-soft"}`} data-testid={`boutique-tab-${k}`}>{l}</button>
                ))}
            </div>

            {tab === "produits" && (
                <div className="space-y-5">
                    {(products || []).map((p) => (
                        <article key={p.product_id} className="rounded-2xl bg-white border border-border p-5 shadow-sm" data-testid={`product-row-${p.product_id}`}>
                            <div className="flex flex-wrap items-start gap-4">
                                <div className="w-24 h-24 rounded-xl overflow-hidden bg-pearl shrink-0">
                                    {p.media?.length > 0 ? (
                                        <img src={`${API}/shop-media/${p.product_id}/${(p.media.find((m) => m.is_primary) || p.media[0]).id}`} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="w-full h-full flex items-center justify-center text-[10px] text-ink-soft">Aucun visuel</span>
                                    )}
                                </div>
                                <div className="flex-1 min-w-[200px]">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-heading font-bold text-ink">{p.name}</h3>
                                        {p.featured && <span className="text-[10px] bg-gold/20 text-gold-dark px-2 py-0.5 rounded-full font-semibold">Vedette</span>}
                                        <span className={`text-xs px-2 py-1 rounded-full ${STATUS[p.status]?.cls}`} data-testid={`product-status-${p.product_id}`}>{STATUS[p.status]?.label}</span>
                                    </div>
                                    <p className="text-xs text-ink-soft mt-1">/boutique/{p.slug} · {p.category || "sans catégorie"} · {p.price_cents != null ? fmtEUR(p.price_cents, p.currency) : "prix non renseigné"} · {p.views || 0} visite(s) · {p.clicks ?? 0} clic(s) marchand</p>
                                    <p className="text-xs text-ink-soft mt-1">Marchands actifs : {(p.merchants || []).filter((m) => m.active && m.url).length} / {(p.merchants || []).length}</p>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => { setForm(fromProduct(p)); setEditing(p); }} data-testid={`product-edit-${p.product_id}`}><Pencil size={13} /> Modifier</Button>
                                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => fileInputs.current[p.product_id]?.click()} data-testid={`product-media-${p.product_id}`}>
                                        <Upload size={13} /> Média
                                    </Button>
                                    <input type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm" className="hidden" ref={(el) => (fileInputs.current[p.product_id] = el)} onChange={(e) => { uploadMedia(p, e.target.files[0]); e.target.value = ""; }} />
                                    {p.status !== "publie" ? (
                                        <Button size="sm" className="h-8 text-xs bg-forest text-ivory gap-1" onClick={() => setStatus(p, "publie")} data-testid={`product-publish-${p.product_id}`}><Eye size={13} /> Publier</Button>
                                    ) : (
                                        <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setStatus(p, "archive")} data-testid={`product-unpublish-${p.product_id}`}><EyeOff size={13} /> Dépublier</Button>
                                    )}
                                    <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => duplicate(p)} data-testid={`product-duplicate-${p.product_id}`}><Copy size={13} /></Button>
                                    <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => setHistory(p)} data-testid={`product-history-${p.product_id}`}><History size={13} /> ({(p.revisions || []).length})</Button>
                                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-destructive border-destructive/40" onClick={() => setDeleteTarget(p)} data-testid={`product-delete-${p.product_id}`}><Trash2 size={13} /></Button>
                                </div>
                            </div>
                            {(p.media || []).length > 0 && (
                                <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
                                    {p.media.map((m) => (
                                        <div key={m.id} className="relative shrink-0 group" data-testid={`product-media-${m.id}`}>
                                            {m.kind === "video" ? (
                                                <span className="w-20 h-20 rounded-lg bg-forest text-ivory text-[10px] font-mono flex items-center justify-center">VIDÉO</span>
                                            ) : (
                                                <img src={`${API}/shop-media/${p.product_id}/${m.id}`} alt="" className={`w-20 h-20 rounded-lg object-cover ${m.is_primary ? "ring-2 ring-gold" : ""}`} />
                                            )}
                                            <div className="absolute inset-0 bg-ink/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                                                {!m.is_primary && <button onClick={() => setPrimary(p, m.id)} className="text-white" title="Image principale" data-testid={`media-primary-${m.id}`}><Star size={14} /></button>}
                                                <button onClick={() => removeMedia(p, m.id)} className="text-red-300" title="Supprimer" data-testid={`media-remove-${m.id}`}><Trash2 size={14} /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </article>
                    ))}
                    {products && products.length === 0 && <p className="rounded-2xl border-2 border-dashed border-gold/50 bg-white p-10 text-center text-ink-soft">Aucun produit. Créez le premier.</p>}
                </div>
            )}

            {tab === "stats" && (
                <div className="rounded-2xl bg-white border border-border shadow-sm overflow-x-auto" data-testid="boutique-stats">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-ink-soft border-b">
                                <th className="p-4">Produit</th><th className="p-4">Statut</th><th className="p-4">Visites (total)</th><th className="p-4">Visites (30 j)</th><th className="p-4">Clics marchands (total)</th><th className="p-4">Clics (30 j)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(stats?.products || []).map((p) => (
                                <tr key={p.product_id} className="border-b last:border-0" data-testid={`stats-row-${p.product_id}`}>
                                    <td className="p-4 font-semibold text-ink">{p.name}</td>
                                    <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${STATUS[p.status]?.cls}`}>{STATUS[p.status]?.label}</span></td>
                                    <td className="p-4">{p.views || 0}</td>
                                    <td className="p-4">{p.views_30d}</td>
                                    <td className="p-4">{p.clicks}</td>
                                    <td className="p-4">{p.clicks_30d}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {stats && stats.products.length === 0 && <p className="p-8 text-center text-ink-soft text-sm">Aucun produit.</p>}
                    <p className="p-4 text-[11px] text-ink-soft border-t">Visites et clics enregistrés uniquement avec le consentement mesure d'audience du visiteur (RGPD).</p>
                </div>
            )}

            <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
                <DialogContent className="bg-ivory max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-editor">
                    <DialogHeader><DialogTitle className="font-heading text-ink">{editing?.product_id ? `Modifier — ${editing.name}` : "Nouveau produit"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
                            <div><Label>Slug (vide = auto)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="product-slug-input" /></div>
                        </div>
                        <div><Label>Description courte</Label><Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="product-short-input" /></div>
                        <div><Label>Description complète (mise en forme premium)</Label><RichTextEditor value={form.description_html || ""} onChange={(v) => setForm({ ...form, description_html: v })} testid="product-description-editor" /></div>
                        <div className="grid grid-cols-3 gap-4">
                            <div><Label>Catégorie</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category-input" /></div>
                            <div><Label>Marque</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
                            <div><Label>Prix TTC (€, vide = non affiché)</Label><Input value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} placeholder="99,00" data-testid="product-price-input" /></div>
                        </div>
                        <div><Label>Bénéfices (un par ligne)</Label><Textarea rows={3} value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} data-testid="product-benefits-input" /></div>
                        <div><Label>Caractéristiques (format : Libellé | Valeur, un par ligne)</Label><Textarea rows={4} value={form.specsText} onChange={(e) => setForm({ ...form, specsText: e.target.value })} data-testid="product-specs-input" /></div>
                        <div><Label>Parcours produit (format : Titre | Texte, un par ligne)</Label><Textarea rows={3} value={form.journeyText} onChange={(e) => setForm({ ...form, journeyText: e.target.value })} /></div>
                        <div className="flex items-center gap-6">
                            <label className="flex items-center gap-2 text-sm text-ink-soft"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-featured-switch" /> Produit vedette</label>
                            <div className="flex items-center gap-2"><Label>Ordre</Label><Input type="number" className="w-20" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 99 })} /></div>
                        </div>
                        <MerchantEditor merchants={form.merchants} onChange={(m) => setForm({ ...form, merchants: m })} />
                        <Button onClick={save} className="w-full bg-forest text-ivory" data-testid="product-save">Enregistrer</Button>
                        {editing?.product_id && <p className="text-xs text-ink-soft text-center">N'oubliez pas de publier le produit pour le rendre visible.</p>}
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!history} onOpenChange={() => setHistory(null)}>
                <DialogContent className="bg-ivory max-w-lg" data-testid="product-history-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-ink">Historique — {history?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                        {(history?.revisions || []).length === 0 && <p className="text-sm text-ink-soft">Aucune modification enregistrée.</p>}
                        {(history?.revisions || []).slice().reverse().map((r, i) => (
                            <div key={i} className="text-xs bg-white border rounded-lg p-3">
                                <p className="text-ink-soft">{fmtDate(r.at)} par {r.by}</p>
                                <p className="text-ink mt-1">Nom : {r.data.name} · Marchands : {(r.data.merchants || []).length}</p>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent className="bg-ivory">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer « {deleteTarget?.name} » ?</AlertDialogTitle>
                        <AlertDialogDescription>Le produit sera archivé et retiré immédiatement de la boutique publique.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="product-delete-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-white" onClick={async () => { try { await api.delete(`/admin/boutique/products/${deleteTarget.product_id}`); toast.success("Produit supprimé."); load(); } catch (e) { toast.error(apiError(e)); } }} data-testid="product-delete-confirm">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
